import {CommonModule} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {AfterViewInit, Component, ElementRef, Inject, Input, NgZone, OnChanges, OnDestroy, signal} from '@angular/core';
import {concatMap, defaultIfEmpty, EMPTY, expand, filter, from, map, Observable, of, Subscription, switchMap, take} from 'rxjs';
import {Router, RouterLink} from '@angular/router';

export interface PureOriginalTuningMatch {
    pureOriginalMatchingDetailsId: number | string;
    tuningName: string;
    hitRate: number | string | null;
    versionWinOLSReference?: string;
    versionId?: number | string;
    dateUploaded?: string | null;
}

export interface TuningPreviewRow extends PureOriginalTuningMatch {
    rate: number | null;
    percentage: number | null;
    tone: 'complete' | 'partial' | 'low' | 'unknown';
    uploadedLabel: string;
}

interface MasterSearchPage {
    content: {id: number | string}[];
    totalPages: number;
}

export function formatTuningDate(value?: string | null): string {
    if (!value) return '—';
    const custom = /^(\d{2})-(\d{2})-(\d{4})-(\d{2})-(\d{2})-(\d{2})$/.exec(value);
    if (custom) return `${custom[1]}.${custom[2]}.${custom[3]} ${custom[4]}:${custom[5]}`;
    const iso = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value);
    return iso ? `${iso[3]}.${iso[2]}.${iso[1]} ${iso[4]}:${iso[5]}` : value;
}

export function sortTuningMatches(matches: PureOriginalTuningMatch[]): TuningPreviewRow[] {
    return matches.map(match => {
        const value = typeof match.hitRate === 'number' || typeof match.hitRate === 'string' && match.hitRate.trim() !== ''
            ? Number(match.hitRate) : NaN;
        const rate = Number.isFinite(value) && value >= 0 && value <= 1 ? value : null;
        return {
            ...match,
            rate,
            uploadedLabel: formatTuningDate(match.dateUploaded),
            // A partial match must never round up to a displayed 100%.
            percentage: rate == null ? null : rate === 1 ? 100 : Math.min(99.99, Math.round(rate * 10000) / 100),
            tone: rate == null ? 'unknown' : rate >= .9 ? 'complete' : rate >= .6 ? 'partial' : 'low',
        } as TuningPreviewRow;
    }).sort((a, b) => (b.rate ?? -1) - (a.rate ?? -1)
        || (a.tuningName || '').localeCompare(b.tuningName || '')
        || Number(a.pureOriginalMatchingDetailsId) - Number(b.pureOriginalMatchingDetailsId));
}

@Component({
    selector: 'app-pure-original-tunings-preview',
    standalone: true,
    imports: [CommonModule, RouterLink],
    templateUrl: './pure-original-tunings-preview.component.html',
    styleUrls: ['./pure-original-tunings-preview.component.css'],
    host: {
        '[style.width.px]': 'viewportWidth()',
        '[style.max-width.px]': 'viewportWidth()'
    }
})
export class PureOriginalTuningsPreviewComponent implements OnChanges, OnDestroy, AfterViewInit {
    @Input({required: true}) pureOriginalId!: number;
    readonly rows = signal<TuningPreviewRow[]>([]);
    readonly loading = signal(false);
    readonly failed = signal(false);
    readonly viewportWidth = signal<number | null>(null);
    readonly openingVersionId = signal<number | null>(null);
    readonly navigationError = signal('');
    private request?: Subscription;
    private versionRequest?: Subscription;
    private viewportObserver?: ResizeObserver;

    constructor(private http: HttpClient, @Inject('API_URL') private apiUrl: string,
                private element: ElementRef<HTMLElement>, private zone: NgZone, private router: Router) {}

    ngOnChanges(): void { this.load(); }
    ngAfterViewInit(): void {
        // The PO table contains dozens of horizontally scrollable columns. Its
        // colspan width is not the width available to this tuning preview.
        const viewport = this.element.nativeElement.closest<HTMLElement>('.table-container');
        if (!viewport) return;
        this.viewportObserver = new ResizeObserver(() => {
            if (viewport.clientWidth > 0) {
                this.zone.run(() => this.viewportWidth.set(viewport.clientWidth));
            }
        });
        this.viewportObserver.observe(viewport);
    }
    ngOnDestroy(): void {
        this.request?.unsubscribe();
        this.versionRequest?.unsubscribe();
        this.viewportObserver?.disconnect();
    }

    load(): void {
        this.request?.unsubscribe();
        this.versionRequest?.unsubscribe();
        this.openingVersionId.set(null);
        this.navigationError.set('');
        this.rows.set([]);
        this.failed.set(false);
        if (!Number.isSafeInteger(this.pureOriginalId) || this.pureOriginalId <= 0) {
            this.loading.set(false);
            this.failed.set(true);
            return;
        }
        this.loading.set(true);
        this.request = this.http.get<PureOriginalTuningMatch[]>(
            `${this.apiUrl}/pureOriginals/${this.pureOriginalId}/matchings`
        ).subscribe({
            next: response => {
                if (!Array.isArray(response)) {
                    this.failed.set(true);
                } else {
                    this.rows.set(sortTuningMatches(response));
                }
                this.loading.set(false);
            },
            error: () => { this.failed.set(true); this.loading.set(false); }
        });
    }

    isValidId(value: unknown): boolean {
        return (typeof value === 'number' || typeof value === 'string' && value.trim() !== '')
            && Number.isSafeInteger(Number(value)) && Number(value) > 0;
    }

    openVersion(value: number | string): void {
        if (!this.isValidId(value) || this.openingVersionId() != null) return;
        const versionId = Number(value);
        this.navigationError.set('');
        this.openingVersionId.set(versionId);
        // Resolve the owning Master only when clicked; Version IDs are not Master IDs.
        this.versionRequest = this.http.get<{masterId?: number; masterWinOLSReference?: string}>(
            `${this.apiUrl}/version/${versionId}`
        ).pipe(
            switchMap(version => this.isValidId(version?.masterId)
                ? of(Number(version.masterId))
                : this.findMasterContainingVersion(versionId, version?.masterWinOLSReference))
        ).subscribe({
            next: masterId => {
                this.openingVersionId.set(null);
                if (!this.isValidId(masterId)) {
                    this.navigationError.set('Could not determine which Master contains this version. Please refresh and try again.');
                    return;
                }
                this.router.navigate(['/app/master-details', masterId], {queryParams: {versionId}});
            },
            error: () => {
                this.openingVersionId.set(null);
                this.navigationError.set('Could not open this version. Please try again.');
            }
        });
    }

    private findMasterContainingVersion(versionId: number, reference?: string): Observable<number | null> {
        if (!reference?.trim()) return of(null);
        // Older APIs return the reference without masterId. A reference may be
        // shared by several Masters, so verify the exact Version ID before routing.
        const visited = new Set<number>();
        const page = (pageNumber: number) => this.http.get<MasterSearchPage>(`${this.apiUrl}/master/searchByVersionWinOls`, {
            params: {versionWinOls: reference, pageNumber, pageSize: 100}
        });
        return page(0).pipe(
            expand((result, index) => index + 1 < result.totalPages ? page(index + 1) : EMPTY, 1),
            concatMap(result => from(result.content ?? [])),
            map(master => Number(master.id)),
            filter(masterId => {
                if (!this.isValidId(masterId) || visited.has(masterId)) return false;
                visited.add(masterId);
                return true;
            }),
            concatMap(masterId => this.http.get<{id: number | string}[]>(
                `${this.apiUrl}/version/versionByMasterId/${masterId}`
            ).pipe(map(versions => Array.isArray(versions) && versions.some(version => Number(version.id) === versionId)
                ? masterId : null))),
            filter(masterId => masterId != null),
            take(1),
            defaultIfEmpty(null)
        );
    }
}
